import { useState, useEffect } from "react";
import { motion, useMotionValueEvent } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { Button } from "../ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d28fa793-233b-4071-af00-df1c1e1b0bba/artifacts/6t4csq6x_Logo_Komel%20AG.png";

const navLinks = [
  { label: "Leistungen", href: "#leistungen" },
  { label: "Referenzen", href: "#referenzen" },
  { label: "Ablauf", href: "#ablauf" },
  { label: "Über uns", href: "#ueber-uns" },
  { label: "Kontakt", href: "#kontakt" },
];

const Header = ({ scrollProgress }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useMotionValueEvent(scrollProgress, "change", (latest) => {
    setIsScrolled(latest > 0.02);
  });

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileOpen(false);
  };

  return (
    <motion.header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? "bg-white/95 backdrop-blur-md shadow-lg" 
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-28 md:h-36 lg:h-44">
          {/* Logo */}
          <a 
            href="/" 
            className="relative z-10"
            data-testid="header-logo"
          >
            <img 
              src={LOGO_URL} 
              alt="Komel AG" 
              className={`h-24 md:h-32 lg:h-40 w-auto transition-all duration-300 ${
                isScrolled ? "" : "brightness-0 invert"
              }`}
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className={`font-medium text-sm tracking-wide transition-colors duration-300 hover:text-komel-accent ${
                  isScrolled ? "text-komel-primary" : "text-white"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </button>
            ))}
            
            <Button
              onClick={() => scrollToSection("#kontakt")}
              className="bg-komel-accent hover:bg-komel-accent-hover text-white font-semibold px-6 py-2.5 rounded-none transition-all duration-300 hover:scale-105"
              data-testid="header-cta-button"
            >
              Offerte anfragen
            </Button>
          </nav>

          {/* Mobile Menu */}
          <div className="lg:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  className={`p-2 ${isScrolled ? "text-komel-primary" : "text-white"}`}
                  data-testid="mobile-menu-button"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-white p-0">
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b">
                    <img 
                      src={LOGO_URL} 
                      alt="Komel AG" 
                      className="h-10 w-auto"
                    />
                  </div>
                  <nav className="flex flex-col p-6 gap-4">
                    {navLinks.map((link) => (
                      <button
                        key={link.href}
                        onClick={() => scrollToSection(link.href)}
                        className="text-left text-lg font-medium text-komel-primary hover:text-komel-accent transition-colors py-2"
                        data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {link.label}
                      </button>
                    ))}
                  </nav>
                  <div className="mt-auto p-6 border-t">
                    <Button
                      onClick={() => scrollToSection("#kontakt")}
                      className="w-full bg-komel-accent hover:bg-komel-accent-hover text-white font-semibold py-4 rounded-none"
                      data-testid="mobile-cta-button"
                    >
                      Offerte anfragen
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
